import { getCookie } from 'cookies-next/client';

import { getVideoType } from '@sola/utils/video.ts';

import { ONETRUST_CATEGORIES, isCategoryOptedOut } from './consent-engine.ts';

export type TrackingVideoInfo = {
  width: number;
  height: number;
  duration: number;
  thumbnailUrl: string;
  title: string;
  uploadDate: string;
};

/**
 * Determine if a video should use a cookieless embed.
 *
 * Returns true when the user has interacted with the consent banner AND
 * has opted out of either performance or targeting cookies. This forces
 * YouTube/Vimeo to use their privacy-respecting embed modes.
 *
 * Returns false (cookies allowed) when the user hasn't interacted yet,
 * or when they've accepted both performance and targeting cookies.
 */
export const cookieFree = (
  optanonConsent?: string,
  optanonAlertBoxClosed?: string,
): boolean => {
  if (!optanonAlertBoxClosed) {
    return false;
  }

  const performanceOptedOut = isCategoryOptedOut(
    optanonConsent,
    ONETRUST_CATEGORIES.PERFORMANCE,
  );
  const targetingOptedOut = isCategoryOptedOut(
    optanonConsent,
    ONETRUST_CATEGORIES.TARGETING,
  );

  return performanceOptedOut || targetingOptedOut;
};

export const cookielessVideoString = (video: string): string => {
  let videoString = video;
  const videoType = getVideoType(video);
  if (videoType === 'youtube') {
    videoString = videoString.replace('https://', '').replace('www.', '');
    videoString = videoString.replace(
      'youtube.com/watch?v=',
      'https://www.youtube-nocookie.com/embed/',
    );
    videoString = videoString.replace(
      'youtu.be/',
      'https://www.youtube-nocookie.com/embed/',
    );
    videoString = videoString.replace(
      'youtube.com/embed/',
      'https://www.youtube-nocookie.com/embed/',
    );
    return videoString;
  }
  if (videoType === 'vimeo') {
    videoString += videoString.includes('?') ? '&DNT=true' : '?DNT=true';
    return videoString;
  }
  return videoString;
};

export const cookieConsentVideoURL = (video: string): string => {
  let videoString = video;
  const requireCookieLessEmbed = cookieFree(
    getCookie('OptanonConsent'),
    getCookie('OptanonAlertBoxClosed'),
  );
  if (requireCookieLessEmbed === false) {
    return videoString;
  }

  videoString = cookielessVideoString(videoString);

  return videoString;
};
